import { useBrandFontFamilies } from '@src/hooks/useBrandFontFamilies';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getUncoveredWorkspaceLanguages } from '@src/utility/fontScriptCoverage';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import Modal from '../common/Modal';
import ProgressBar from '../common/ProgressBar';
import SpinningLoading from '../SpinningLoading';
import FontFamilyCard from './FontFamilyCard';
import useBrandKitFontUpload, {
  parseFontData,
  useFontDropzone,
} from './utils/useBrandKitFontUpload';

import type {
  BrandingCustomFont,
  BrandingFontMetadata,
  FontFamily,
  FontVariant,
} from '../../types.ts/story';
import type { Language } from '@src/types.ts/language';

enum FontsDialogState {
  display,
  acknowledge,
  uploading,
  duplicate,
}

type FontData = {
  file: File;
  fontData: BrandingFontMetadata;
};

type Props = {
  onClose: () => void;
  fontsToUpload: File[] | null;
};

const BrandKitCustomFontsDialog = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const { onClose, fontsToUpload: _fontsToUpload } = props;

  const branding = videoCreator.organization?.branding;
  const customFonts = filterAndSortFonts(branding?.customFonts);
  // Shared resolver — archived entries stripped; legacy customFonts fallback.
  // See useBrandFontFamilies. (Previously a drifted local copy that didn't
  // strip archived variants.)
  const fontFamilies = useBrandFontFamilies();

  const [fontsToUpload, setFontsToUpload] = useState<File[] | null>(
    _fontsToUpload || null,
  );
  const {
    uploadFontsToBrandKit,
    uploading,
    archiveFontFromBrandKit,
    archiving,
  } = useBrandKitFontUpload(
    videoCreator,
    datoClientStore.assetRepository,
    datoClientStore.albumRepository,
  );

  const [dialogState, setDialogState] = useState(
    fontsToUpload ? FontsDialogState.acknowledge : FontsDialogState.display,
  );
  const [fontToDelete, setFontToDelete] = useState<BrandingCustomFont | null>(
    null,
  );

  if (fontToDelete) {
    return (
      <Modal isOpen closeModal={() => setFontToDelete(null)}>
        <SDeleteContainer>
          <div>
            <h1>Delete font?</h1>
            <h2>This cannot be undone.</h2>
          </div>
          <p>
            You are about to delete <strong>“{fontToDelete.fullName}”</strong>{' '}
            from the current Brand Kit. You will need to reupload it if you want
            to use it again. Existing designs won’t be affected.
          </p>
          <SButton
            className="negative"
            disabled={archiving}
            onClick={async () => {
              await archiveFontFromBrandKit(fontToDelete);
              setFontToDelete(null);
            }}
          >
            Delete permanently
          </SButton>
        </SDeleteContainer>
      </Modal>
    );
  }

  return (
    <Modal isOpen closeModal={onClose}>
      <SContainer>
        <h2>Custom Fonts</h2>
        {dialogState === FontsDialogState.acknowledge ? (
          <FontAcknowledgeContent
            onClose={onClose}
            onUpload={async () => {
              if (!fontsToUpload?.length) return;
              // Check for duplicates
              const { duplicates } = await getFontDuplicates(
                fontsToUpload,
                customFonts,
              );
              if (duplicates.length) {
                setDialogState(FontsDialogState.duplicate);
                return;
              }
              // Proceed to upload
              setDialogState(FontsDialogState.uploading);
              uploadFontsToBrandKit(fontsToUpload);
            }}
          />
        ) : dialogState === FontsDialogState.duplicate ? (
          <FontDuplicatesContent
            customFonts={customFonts}
            fontsToUpload={fontsToUpload}
            onClose={onClose}
            onSkipDuplicates={async () => {
              if (!fontsToUpload?.length) return;
              // Skip duplicates and upload the rest
              const { rest } = await getFontDuplicates(
                fontsToUpload,
                customFonts,
              );
              setDialogState(FontsDialogState.uploading);
              uploadFontsToBrandKit(rest.map(({ file }) => file));
            }}
            onUploadAll={async () => {
              if (!fontsToUpload?.length) return;
              setDialogState(FontsDialogState.uploading);
              uploadFontsToBrandKit(fontsToUpload);
            }}
          />
        ) : dialogState === FontsDialogState.uploading ? (
          <FontAcknowledgeUploading uploading={uploading} onUpload={onClose} />
        ) : (
          <FontUploadContent
            fontFamilies={fontFamilies}
            supportedLanguages={videoCreator.organization?.supportedLanguages}
            onDrop={async acceptedFiles => {
              if (!acceptedFiles.length) return;
              setFontsToUpload(acceptedFiles);
              setDialogState(FontsDialogState.acknowledge);
            }}
            onDeleteVariant={(variant: FontVariant) => {
              const match = branding?.customFonts?.find(
                f => f.artifactId === variant.artifactId,
              );
              if (match) setFontToDelete(match);
            }}
          />
        )}
      </SContainer>
    </Modal>
  );
});

const filterAndSortFonts = (fonts: BrandingCustomFont[] | undefined | null) =>
  fonts
    ?.filter(font => !font.archivedAt)
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

const FontUploadContent = ({
  fontFamilies,
  onDrop,
  onDeleteVariant,
  supportedLanguages,
}: {
  fontFamilies: FontFamily[];
  onDrop: (acceptedFiles: File[]) => void;
  onDeleteVariant: (variant: FontVariant) => void;
  supportedLanguages?: Language[];
}) => {
  const { getRootProps, getInputProps, open } = useFontDropzone({
    onDrop,
  });
  const uploadButton = (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <SButton onClick={open}>Upload</SButton>
    </div>
  );
  // `fontFamilies` comes from useBrandFontFamilies, which already strips
  // archived variants and drops fully-archived families — so it's the
  // active set directly (no extra filtering needed here).
  return (
    <SUploadContent>
      <SHeading>Uploaded Fonts</SHeading>
      {fontFamilies.length ? (
        <SFamilyList>
          {fontFamilies.map(family => (
            <FontFamilyCard
              key={family.id}
              family={family}
              onDeleteVariant={onDeleteVariant}
              // PLAT-11412 — workspace languages the family can't display.
              uncoveredLanguages={getUncoveredWorkspaceLanguages(
                family.variants.filter(v => !v.archivedAt),
                supportedLanguages,
              )}
            />
          ))}
        </SFamilyList>
      ) : (
        <p className="muted">
          Click the button below to add your first custom font!
        </p>
      )}
      {uploadButton}
    </SUploadContent>
  );
};

const FontAcknowledgeContent = ({
  onClose,
  onUpload,
}: {
  onClose: () => void;
  onUpload: () => void;
}) => {
  return (
    <SUploadContent>
      <SHeading>Please confirm</SHeading>
      <p>
        By uploading custom fonts, I acknowledge that I am the owner or have the
        right to use these fonts for my intended purpose.
      </p>
      <SUploadActions>
        <SButton className="outline" onClick={onClose}>
          Cancel
        </SButton>
        <SButton onClick={onUpload}>Yes, upload</SButton>
      </SUploadActions>
    </SUploadContent>
  );
};

const getFontDuplicates = async (
  fontsToUpload: File[],
  customFonts: BrandingCustomFont[] | undefined,
) => {
  if (!customFonts) return { duplicates: [], rest: [] };
  const parsed = await Promise.all(
    fontsToUpload.map(async file => {
      const fontData = await parseFontData(file);
      return { file, fontData };
    }),
  );
  const fontDuplicatesData = parsed.reduce(
    (acc, item) => {
      const isDuplicate = customFonts.some(
        font => font.postScriptName === item.fontData.postScriptName,
      );
      if (isDuplicate) acc.duplicates.push(item);
      else acc.rest.push(item);
      return acc;
    },
    { duplicates: [], rest: [] } as {
      duplicates: FontData[];
      rest: FontData[];
    },
  );
  return fontDuplicatesData;
};

const FontDuplicatesContent = ({
  customFonts,
  fontsToUpload,
  onClose,
  onSkipDuplicates,
  onUploadAll,
}: {
  customFonts?: BrandingCustomFont[];
  fontsToUpload: File[] | null;
  onClose: () => void;
  onSkipDuplicates: () => void;
  onUploadAll: () => void;
}) => {
  const [fontsData, setFontsData] = useState<{
    duplicates: FontData[];
    rest: FontData[];
  } | null>(null);
  useEffect(() => {
    if (!fontsToUpload) return;
    getFontDuplicates(fontsToUpload, customFonts).then(setFontsData);
  }, [customFonts, fontsToUpload]);
  if (!fontsData)
    return (
      <SUploadContent center>
        <SpinningLoading text={null} customStyle={{ position: 'relative' }} />
      </SUploadContent>
    );
  return (
    <SUploadContent>
      <SHeading>Duplicate Fonts</SHeading>
      <p className="left">
        You're trying to upload fonts that have already been added to your brand
        kit. Please review:
      </p>
      <SUploadContentBlock>
        <div className="heading">Already in your kit:</div>
        {fontsData.duplicates.map(({ fontData }) => (
          <div className="row">{fontData.fullName}</div>
        ))}
      </SUploadContentBlock>
      <SUploadContentBlock>
        <div className="heading">Fonts you're trying to upload:</div>
        {fontsData.duplicates.map(({ fontData }) => (
          <div className="row">{fontData.fullName}</div>
        ))}
        {fontsData.rest.map(({ fontData }) => (
          <div className="row">{fontData.fullName}</div>
        ))}
      </SUploadContentBlock>
      <SUploadActions>
        <SButton className="outline" onClick={onClose}>
          Cancel
        </SButton>
        {fontsData.rest.length ? (
          <SButton className="outline" onClick={onSkipDuplicates}>
            Skip Duplicates
          </SButton>
        ) : null}
        <SButton onClick={onUploadAll}>Upload All</SButton>
      </SUploadActions>
    </SUploadContent>
  );
};

const FontAcknowledgeUploading = ({
  uploading,
  onUpload,
}: {
  uploading: boolean;
  onUpload: () => void;
}) => {
  useEffect(() => {
    if (!uploading) setTimeout(onUpload, 500);
  }, [uploading]);
  return (
    <SUploadContent center>
      <SHeading>Uploading fonts</SHeading>
      <p>Just a moment...</p>
      <ProgressBar auto width={320} finished={!uploading} />
    </SUploadContent>
  );
};

const SContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 440px;
  max-height: 70vh;
  overflow-y: auto;
  padding: 48px 24px;
  border-radius: 16px;
  border: 1px solid var(--border);
  background-color: var(--background);
  box-shadow: var(--elevation-toast);
  color: var(--text-secondary);
  > h2 {
    color: var(--foreground);
    font-size: var(--text-md);
    font-weight: var(--font-weight-semi-bold);
    margin: 0;
  }
  > .fonts-dialog-content {
    display: flex;
    flex-direction: column;
  }
`;

const SDeleteContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 328px;
  padding: 24px 16px;
  border-radius: 16px;
  border: 1px solid var(--border);
  background-color: var(--background);
  box-shadow: var(--elevation-toast);
  color: var(--text-secondary);
  h1 {
    margin: 0;
    color: var(--foreground);
    text-align: center;
    font-size: 32px;
    font-weight: var(--font-weight-bold);
    line-height: 120%;
  }
  h2 {
    margin: 4px 0 0;
    color: var(--foreground);
    text-align: center;
    font-size: 16px;
    font-weight: var(--font-weight-bold);
  }
  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-regular);
  }
`;

const SHeading = styled.h1`
  margin: 0;
  color: var(--foreground);
  text-align: center;
  font-size: 32px;
  font-weight: var(--font-weight-bold);
  line-height: 120%;
`;

const SUploadContent = styled.div<{ center?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ center }) =>
    center &&
    css`
      align-items: center;
    `}
  > p {
    margin: 0;
    color: var(--text-secondary);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    text-align: center;
    &.muted {
      color: var(--text-muted);
    }
    &.left {
      text-align: left;
    }
  }
`;
const SButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 48px;
  padding: 16px;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: var(--background);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  text-align: center;
  cursor: pointer;
  transition: var(--transition-normal);
  &:hover:not(:disabled) {
    filter: brightness(1.1);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &.outline {
    background: none;
    border: 1px solid var(--primary);
    color: var(--primary);
  }
  &.negative {
    background: var(--toast-error);
  }
`;
const SUploadActions = styled.div`
  display: flex;
  gap: 8px;
`;
const SFamilyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const SUploadContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  > .heading {
    color: var(--foreground);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-bold);
  }
  > .row {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
  }
`;

export default BrandKitCustomFontsDialog;
